import { z } from "zod";

/**
 * Admin form schema for create/edit of an event location.
 *
 * Locations are the canonical pin records — every entity that puts
 * something on the map references one of these. Coordinates are
 * nullable: a label-only entry (e.g. "Conference Room 203") is a valid
 * location with no map pin.
 *
 * Address details (street / city / state / country) auto-fill from the
 * MapKit Place result when the organizer picks a real venue, but each
 * field stays independently editable so the organizer can correct,
 * override, or fill in pieces MapKit didn't return.
 *
 * Color stores a hex string. The picker sets a smart default at creation
 * time based on context (category color when created from
 * SessionForm, etc.); the organizer can override.
 */
export const locationFormSchema = z.object({
  label: z.string().min(1, "Label is required"),
  coordinates: z
    .object({
      lat: z.number().min(-90).max(90),
      lng: z.number().min(-180).max(180),
    })
    .nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  stateOrRegion: z.string().nullable(),
  country: z.string().nullable(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a valid hex color (e.g., #5C6BC0)"),
  verified: z.boolean(),
});

export type LocationFormData = z.infer<typeof locationFormSchema>;
